// src/components/tasks/TaskDetail/index.tsx
// 重構後的 TaskDetail 主組件 - 使用 Local-first 樂觀更新

import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Task, TaskStatus, TaskCategory, TaskCloseReason } from '../../../types/task';
import { ArrowLeft } from '../../common/icons';
import ResponsiveModal from '../../common/ResponsiveModal';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorState from '../../common/ErrorState';
import TaskSplitModal from '../TaskSplitModal';
import ObjectivePicker from '../ObjectivePicker';
import ConfirmDialog from '../../common/ConfirmDialog';
import SimpleDatePicker from '../../common/SimpleDatePicker';
import ButtonGroupPicker from '../../common/ButtonGroupPicker';
import DurationPicker from '../../common/DurationPicker';
import { useToast } from '../../../contexts/ToastContext';
import { useObjectives } from '../../../features/goal/contexts/ObjectiveContext';
import { useTaskDetail } from '../../../hooks/useTaskDetail';
import { useTaskDetailModals } from '../../../hooks/useTaskDetailModals';
import { TaskDetailContent } from './TaskDetailContent';
import { TaskDetailMetadata } from './TaskDetailMetadata';
import { TaskDetailActions } from './TaskDetailActions';

// ============================================
// Props 接口
// ============================================

interface TaskDetailProps {
  isOpen: boolean;
  taskId: string | null;
  initialTask?: Task;
  onClose: () => void;
  onStatusChange?: (taskId: string, newStatus: TaskStatus, category?: TaskCategory, closeReason?: TaskCloseReason) => void;
  onSplitComplete?: (taskId: string) => void;
  onDropped?: (droppedTask: Task) => void;
  mode?: 'create' | 'edit';
  initialTitle?: string;
}

// ============================================
// 主組件
// ============================================

const TaskDetail: React.FC<TaskDetailProps> = ({
  isOpen,
  taskId,
  initialTask,
  onClose,
  onStatusChange,
  onSplitComplete,
  onDropped,
  mode = 'edit',
  initialTitle
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { objectives } = useObjectives();
  const actionsButtonRef = useRef<HTMLButtonElement>(null);

  // 使用自定義 Hooks
  const {
    task,
    isLoading,
    error,
    splitTaskData,
    objectiveTitle,
    toggleStatus,
    dropTask,
    deleteTask,
    updatePlan,
    updateTargetDate,
    updateTitle,
    updateNotes,
    updateDuration,
    updateCategory,
    updateObjective,
    updatePriority,
    updateEnergy,
    updateLocation,
    requestSplit,
    handleSplitComplete,
  } = useTaskDetail({
    taskId,
    isOpen,
    initialTask,
    objectives,
    onStatusChange,
    onSplitComplete,
    onDropped,
    onClose,
    showSuccess,
    showError,
    mode,
    initialTitle
  });

  const { modals, openModal, closeModal, toggleModal } = useTaskDetailModals();

  // create mode 下 task 尚未建立時用 initialTitle 作為臨時標題，建立後跟隨 task.title
  const displayTitle = task?.title ?? initialTitle ?? '';

  // ============================================
  // 事件處理
  // ============================================

  /**
   * Schedule 按鈕點擊：關閉 TaskDetail 並跳轉到 Calendar 頁面
   *
   * 透過 URL 參數傳遞 highlightTaskId 和 date，
   * Calendar 頁面會自動導航到對應日期並高亮該任務卡片。
   */
  const handleScheduleClick = () => {
    if (!task) return;

    // 關閉 TaskDetail，跳轉到 Calendar Page
    onClose();

    const params = new URLSearchParams();
    params.set('highlightTaskId', task.id);
    if (task.plan) {
      params.set('date', task.plan);
    }

    navigate(`/calendar?${params.toString()}`);
  };

  const handleSplitClick = async () => {
    openModal('split');
    await requestSplit();
  };

  // ============================================
  // 渲染邏輯
  // ============================================

  const renderContent = () => {
    if (isLoading) {
      return <LoadingSpinner className="h-32" />;
    }

    if (error) {
      return <div className="p-4"><ErrorState message={error} /></div>;
    }

    // edit mode 下 task=null 是異常狀態
    if (!task && mode !== 'create') {
      return (
        <div className="flex justify-center items-center h-32 text-gray-500">
          No task selected
        </div>
      );
    }

    // create mode 下 task=null 時，用 placeholder task 渲染 UI
    // placeholder 讓 TaskDetailContent / TaskDetailMetadata 不需要感知 null
    const displayTask: Task = task ?? {
      id: '',
      title: displayTitle,
      status: 'OPEN' as const,
      userId: '',
      createdAt: new Date(),
      migrationCount: 0,
    };

    // task 尚未建立（create mode pending）：隱藏底部 action bar
    const showActions = !!task;

    return (
      <div className="flex flex-col h-full">
        {/* Main content area - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
          <div className="flex flex-col md:flex-row md:gap-6 min-h-full">
            {/* Left Column: Title + Description */}
            <TaskDetailContent
              task={displayTask}
              onUpdateTitle={updateTitle}
              onUpdateNotes={updateNotes}
              initialEditMode={mode === 'create'}
              forceCallOnBlur={mode === 'create' && !task}
            />

            {/* Right Column: Metadata (Desktop) */}
            <TaskDetailMetadata
              task={displayTask}
              objectiveTitle={objectiveTitle}
              variant="desktop"
              onPlanClick={() => openModal('planSelector')}
              onBeforeClick={() => openModal('beforeSelector')}
              onDurationClick={() => openModal('durationPicker')}
              onCategoryClick={() => openModal('categoryPicker')}
              onObjectiveClick={() => openModal('objectivePicker')}
              onPrioritySave={updatePriority}
              onEnergySave={updateEnergy}
              onLocationSave={updateLocation}
            />

            {/* Metadata (Mobile) */}
            <TaskDetailMetadata
              task={displayTask}
              objectiveTitle={objectiveTitle}
              variant="mobile"
              onPlanClick={() => openModal('planSelector')}
              onBeforeClick={() => openModal('beforeSelector')}
              onDurationClick={() => openModal('durationPicker')}
              onCategoryClick={() => openModal('categoryPicker')}
              onObjectiveClick={() => openModal('objectivePicker')}
              onPrioritySave={updatePriority}
              onEnergySave={updateEnergy}
              onLocationSave={updateLocation}
            />
          </div>
        </div>

        {/* Bottom action area - create mode 下 task 建立後才顯示 */}
        {showActions && (
          <div className="shrink-0 border-t border-gray-100 px-4 py-3 sm:py-4 bg-white">
            <TaskDetailActions
              task={task}
              onSchedule={handleScheduleClick}
              onSplit={handleSplitClick}
              onToggleStatus={toggleStatus}
              onDrop={() => openModal('dropConfirm')}
              onDelete={() => openModal('deleteConfirm')}
              showActionsMenu={modals.actionsMenu}
              onToggleActionsMenu={() => toggleModal('actionsMenu')}
              onCloseActionsMenu={() => closeModal('actionsMenu')}
              actionsButtonRef={actionsButtonRef}
            />
            <div className="h-safe-bottom" />
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        title={mode === 'create' ? 'New Task' : 'Task Details'}
        leftAction={
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
        }
      >
        {renderContent()}
      </ResponsiveModal>

      {/* Task Split Modal */}
      {task && modals.split && (
        <TaskSplitModal
          isOpen={modals.split}
          onClose={() => closeModal('split')}
          task={task}
          initialSubtasks={splitTaskData.subtasks}
          isLoading={splitTaskData.isLoading}
          error={splitTaskData.error}
          onSplitComplete={handleSplitComplete}
        />
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={modals.deleteConfirm}
        type="delete"
        title="Confirm Delete?"
        message="This action cannot be undone. The task will be permanently deleted."
        confirmText="Delete"
        onConfirm={async () => {
          await deleteTask();
          closeModal('deleteConfirm');
        }}
        onCancel={() => closeModal('deleteConfirm')}
      />

      {/* Drop Confirm Dialog */}
      <ConfirmDialog
        isOpen={modals.dropConfirm}
        type="warning"
        title="Drop this task?"
        message="This task will be marked as dropped."
        confirmText="Drop"
        onConfirm={async () => {
          await dropTask();
          closeModal('dropConfirm');
        }}
        onCancel={() => closeModal('dropConfirm')}
      />

      {/* Plan Selector */}
      {modals.planSelector && (
        <SimpleDatePicker
          currentValue={task?.plan}
          onSelect={async (dateString) => {
            await updatePlan(dateString);
            closeModal('planSelector');
          }}
          onClose={() => closeModal('planSelector')}
        />
      )}

      {/* Before (TargetDate) Selector */}
      {modals.beforeSelector && (
        <SimpleDatePicker
          currentValue={task?.targetDate}
          onSelect={async (dateString) => {
            await updateTargetDate(dateString);
            closeModal('beforeSelector');
          }}
          onClose={() => closeModal('beforeSelector')}
        />
      )}

      {/* Duration Picker */}
      {modals.durationPicker && (
        <DurationPicker
          currentMinutes={task?.estimatedTime}
          onSelect={async (minutes) => {
            await updateDuration(minutes ?? null);
            closeModal('durationPicker');
          }}
          onClose={() => closeModal('durationPicker')}
        />
      )}

      {/* Category Picker */}
      {modals.categoryPicker && (
        <ButtonGroupPicker<TaskCategory>
          currentValue={task?.category}
          options={[
            { value: 'work', label: 'Work', color: 'blue' },
            { value: 'personal', label: 'Personal', color: 'yellow' },
            { value: 'family', label: 'Family', color: 'green' },
            { value: 'health', label: 'Health', color: 'purple' },
            { value: 'social', label: 'Social', color: 'pink' },
          ]}
          onSelect={async (category) => {
            await updateCategory(category ?? null);
            closeModal('categoryPicker');
          }}
          onClose={() => closeModal('categoryPicker')}
        />
      )}

      {/* Objective Picker */}
      {modals.objectivePicker && (
        <ObjectivePicker
          objectives={objectives.filter(o => o.status === 'active')}
          currentObjectiveId={task?.objectiveId}
          onSelect={updateObjective}
          onClose={() => closeModal('objectivePicker')}
        />
      )}

      {/* Priority and Energy now use inline select in MetadataRow */}
    </>
  );
};

export default TaskDetail;
